import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import { maps } from "api/api-maps";
import type {
  INotification,
  ICreateNotificationRequest,
  ICreateNotificationResponse,
  INotificationAttachment,
  IMentionContact,
} from "./api-notifications.d";

export const notifications = createApi({
  reducerPath: "notifications",
  tagTypes: ["NOTIFICATIONS", "ENTITY_COMMENT_COUNTS"],

  baseQuery: axiosBaseQuery({
    // Use API v2 base URL; endpoints will include the `/notifications` path explicitly
    baseUrl: `${VITE_API_V2_URL}`,
  }),
  endpoints: (builder) => ({
    getNotifications: builder.query<INotification[], void>({
      query: () => ({
        url: `/notifications`,
        method: "get",
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ["NOTIFICATIONS"],
    }),
    updateMarkAsRead: builder.mutation<INotification, string>({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: "put",
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    updateMarkAsReadAll: builder.mutation<INotification[], void>({
      query: () => ({
        url: `/notifications/read-all`,
        method: "put",
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    updateMarkAsReadAllByEntity: builder.mutation<{ success: boolean }, string>(
      {
        query: (entityId) => ({
          url: `/notifications/entity/${encodeURIComponent(entityId)}/read-all`,
          method: "patch",
        }),
        invalidatesTags: ["NOTIFICATIONS"],
      },
    ),
    sendOpportunityPriorityNotification: builder.mutation<
      { success: boolean },
      {
        opportunity_id: string;
        opportunity_name: string;
        client_name?: string;
        internal_name?: string;
        contact_id?: string;
        priority: boolean;
      }
    >({
      query: (body) => ({
        url: `/notifications/opportunity-priority-notification`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    getEntityNotifications: builder.query<INotification[], string>({
      query: (entityId) => ({
        url: `/notifications/entity/${encodeURIComponent(entityId)}`,
        method: "get",
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ["NOTIFICATIONS"],
    }),
    getEntityUnreadCount: builder.query<{ count: number }, string>({
      query: (entityId) => ({
        url: `/notifications/entity/${encodeURIComponent(entityId)}/unread-count`,
        method: "get",
      }),
      transformResponse: (response: any) => {
        const d = response?.data;
        return typeof d?.count === "number" ? d : (d?.data ?? { count: 0 });
      },
      providesTags: ["NOTIFICATIONS"],
    }),
    /** Batch thread comment counts for list badges (reuse outside MAP list). */
    getEntityCommentCounts: builder.query<Record<string, number>, string[]>({
      query: (entityIds) => {
        const ids = entityIds.filter(Boolean);
        return {
          url: `/notifications/entity-comment-counts?entity_ids=${encodeURIComponent(
            ids.join(","),
          )}`,
          method: "get",
        };
      },
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;
        const counts = payload?.counts ?? payload?.data?.counts ?? {};
        return typeof counts === "object" && counts !== null ? counts : {};
      },
      providesTags: ["ENTITY_COMMENT_COUNTS"],
    }),
    searchMentionContacts: builder.query<IMentionContact[], string>({
      query: (q) => ({
        url: `/notifications/mention-contacts?q=${encodeURIComponent(q)}`,
        method: "get",
      }),
      transformResponse: (response: any) => {
        // Base query returns { data: axiosResult.data }; backend returns { data: [...] }
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response?.data?.data;
        return Array.isArray(list) ? list : [];
      },
      providesTags: ["NOTIFICATIONS"],
    }),
    createNotification: builder.mutation<
      ICreateNotificationResponse,
      ICreateNotificationRequest
    >({
      query: (body) => ({
        // POST /api/v2/notifications
        url: `/notifications`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["NOTIFICATIONS", "ENTITY_COMMENT_COUNTS"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh MAP list badges (comment_count embedded in /maps response)
          dispatch(maps.util.invalidateTags(["MAP_LIST"]));
        } catch {
          // mutation error — no badge refresh
        }
      },
    }),
    sendMapRejectionNotification: builder.mutation<
      { success: boolean },
      {
        map_name: string;
        department: string;
        comment: string;
        rm_contact_id?: string;
      }
    >({
      query: (body) => ({
        url: `/notifications/map-rejection`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    sendMapResubmitNotification: builder.mutation<
      { success: boolean },
      {
        map_name: string;
        comment: string;
        exception_authority: string;
      }
    >({
      query: (body) => ({
        url: `/notifications/map-resubmit`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["NOTIFICATIONS"],
    }),
    uploadNotificationAttachments: builder.mutation<
      INotificationAttachment[],
      { files: File[] }
    >({
      query: ({ files }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });

        return {
          url: `/notifications/attachments/upload`,
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      // Backend wraps payload as { data: [...] }, unwrap it here so callers
      // receive a plain attachments array.
      transformResponse: (response: any) => response.data,
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useUpdateMarkAsReadMutation,
  useUpdateMarkAsReadAllMutation,
  useUpdateMarkAsReadAllByEntityMutation,
  useSendOpportunityPriorityNotificationMutation,
  useGetEntityNotificationsQuery,
  useGetEntityUnreadCountQuery,
  useGetEntityCommentCountsQuery,
  useSearchMentionContactsQuery,
  useLazySearchMentionContactsQuery,
  useCreateNotificationMutation,
  useUploadNotificationAttachmentsMutation,
  useSendMapRejectionNotificationMutation,
  useSendMapResubmitNotificationMutation,
} = notifications;
